import React, { useContext, useEffect, useState } from "react";

import { Paper } from "@material-ui/core";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";

import { useStyles } from "./style";

import {
  EnhancedTableToolbar,
  CardItem,
} from "../../../components/pages/reports/interpreterInvoiceReport/index";

import { InterpreterInvoiceReportContext } from "../../../context/states/reports/interpreterInvoiceState";

const initialSearchState = {
  vatNo: "",
  businessName: "",
  interpreter: null,
  company: null,

  private: false,
  hasVat: false,

  fromDate: null,
  toDate: null,

  isSearch: false,
};

const InterpreterInvoiceReport = () => {
  const classes = useStyles();
  const matches = useMediaQuery("(min-width:1920px)");

  const {
    interpreterInvoices,
    totalAmount,
    totalVAT,
    totalInvoice,

    interpreters,
    clients,

    count,
    loading,

    getInterpretersHandler,
    getClientsHandler,
    getInterpreterInvoiceReportHandler,
  } = useContext(InterpreterInvoiceReportContext);

  const [dense] = useState(true);

  /*
   * ==========================================================
   * ADVANCED SEARCH OPEN/CLOSE
   * ==========================================================
   */
  const [isAdvancedSearch, setIsAdvancedSearch] = useState(false);

  /*
   * ==========================================================
   * SINGLE SOURCE OF TRUTH FOR ALL FILTERS
   * ==========================================================
   */
  const [isSearchObj, setIsSearchObj] = useState({
    ...initialSearchState,
  });

  /*
   * ==========================================================
   * INITIAL REPORT
   * ==========================================================
   */
  useEffect(() => {
    getInterpreterInvoiceReportHandler("");

    // We only want this on initial mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * ==========================================================
   * ADVANCED SEARCH OPEN/CLOSE
   * ==========================================================
   */
  const handleAdvancedSearchToggle = () => {
    setIsAdvancedSearch((prev) => !prev);
  };

  const closeAdvancedSearch = () => {
    setIsAdvancedSearch(false);
  };

  /*
   * ==========================================================
   * BUILD QUERY FROM ALL ACTIVE FILTERS
   * ==========================================================
   */
  const buildSearchQuery = (filters) => {
    const params = [];

    /*
     * VAT NO
     */
    if (filters.vatNo) {
      params.push(`interpreterVatNumber=${encodeURIComponent(filters.vatNo)}`);
    }

    /*
     * BUSINESS NAME
     */
    if (filters.businessName) {
      params.push(
        `interpreterBusinessName=${encodeURIComponent(filters.businessName)}`,
      );
    }

    /*
     * INTERPRETER
     */
    if (filters.interpreter?._id) {
      params.push(`interpreter=${encodeURIComponent(filters.interpreter._id)}`);
    }

    /*
     * COMPANY
     */
    if (filters.company?._id) {
      params.push(`companyId=${encodeURIComponent(filters.company._id)}`);
    }

    /*
     * PRIVATE
     */
    if (filters.private) {
      params.push("businessType=Private");
    }

    /*
     * VAT
     */
    if (filters.hasVat) {
      params.push("hasVat=true");
    }

    /*
     * FROM DATE
     */
    if (filters.fromDate) {
      const fromDate = new Date(filters.fromDate);

      if (!Number.isNaN(fromDate.getTime())) {
        params.push(`fromDate=${encodeURIComponent(fromDate.toISOString())}`);
      }
    }

    /*
     * TO DATE
     */
    if (filters.toDate) {
      const toDate = new Date(filters.toDate);

      if (!Number.isNaN(toDate.getTime())) {
        params.push(`toDate=${encodeURIComponent(toDate.toISOString())}`);
      }
    }

    return params.join("&");
  };

  /*
   * ==========================================================
   * ADVANCED SEARCH
   *
   * Only replaces:
   *   vatNo
   *   businessName
   *   interpreter
   *   company
   *
   * It PRESERVES:
   *   private
   *   hasVat
   *   fromDate
   *   toDate
   * ==========================================================
   */
  const searchAdvancedHandler = (searchData) => {
    const mergedFilters = {
      ...isSearchObj,

      vatNo: searchData.vatNo || "",
      businessName: searchData.businessName || "",
      interpreter: searchData.interpreter || null,
      company: searchData.company || null,
      isSearch: true,
    };

    const query = buildSearchQuery(mergedFilters);

    setIsSearchObj(mergedFilters);

    getInterpreterInvoiceReportHandler(query);

    setIsAdvancedSearch(false);
  };

  /*
   * ==========================================================
   * RIGHT-SIDE QUICK FILTER SEARCH
   *
   * Only replaces:
   *   private
   *   hasVat
   *   fromDate
   *   toDate
   *
   * It PRESERVES:
   *   vatNo
   *   businessName
   *   interpreter
   *   company
   * ==========================================================
   */
  const searchHandler = (quickFilters = {}) => {
    const mergedFilters = {
      ...isSearchObj,

      private: quickFilters.businessType === "Private",

      hasVat: !!quickFilters.hasVat,

      fromDate: quickFilters.fromDate || null,

      toDate: quickFilters.toDate || null,

      isSearch: true,
    };

    const query = buildSearchQuery(mergedFilters);

    setIsSearchObj(mergedFilters);

    getInterpreterInvoiceReportHandler(query);
  };

  /*
   * ==========================================================
   * CLEAR ALL FILTERS
   * ==========================================================
   */
  const clearSearchHandler = () => {
    setIsSearchObj({
      ...initialSearchState,
    });

    setIsAdvancedSearch(false);

    getInterpreterInvoiceReportHandler("");
  };

  /*
   * ==========================================================
   * CLEAR ADVANCED SEARCH
   *
   * In this version CLEAR means clear all filters,
   * so the report returns to its original state.
   * ==========================================================
   */
  const clearAdvancedSearchHandler = () => {
    setIsSearchObj({
      ...initialSearchState,
    });

    setIsAdvancedSearch(false);

    getInterpreterInvoiceReportHandler("");
  };

  /*
   * ==========================================================
   * COMPANY AUTOCOMPLETE SEARCH
   * ==========================================================
   */
  const handleInputChange = (event) => {
    const value = event?.target?.value || "";

    if (!value) {
      return;
    }

    getClientsHandler(value);
  };

  /*
   * ==========================================================
   * INTERPRETER AUTOCOMPLETE SEARCH
   * ==========================================================
   */
  const handleInterpreterInputChange = (event) => {
    const value = event?.target?.value || "";

    if (!value) {
      return;
    }

    getInterpretersHandler(value);
  };

  return (
    <React.Fragment>
      <div
        style={{
          width: "100%",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Paper className={classes.paper}>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <EnhancedTableToolbar
              title="Interpreter Invoice Report"
              /*
               * Totals
               */
              count={count}
              totalAmount={totalAmount}
              totalVAT={totalVAT}
              totalInvoice={totalInvoice}
              /*
               * Advanced Search
               */
              isAdvancedSearch={isAdvancedSearch}
              handleAdvancedSearch={handleAdvancedSearchToggle}
              closeAdvancedSearch={closeAdvancedSearch}
              clearAdvancedSearchHandler={clearAdvancedSearchHandler}
              searchAdvancedHandler={searchAdvancedHandler}
              isClients={clients}
              isInterpreters={interpreters}
              handleInputChange={handleInputChange}
              handleInterpreterInputChange={handleInterpreterInputChange}
              isSearchObj={isSearchObj}
              /*
               * Right-side filters
               */
              searchHandler={searchHandler}
              clearSearchHandler={clearSearchHandler}
            />
          </MuiPickersUtilsProvider>

          <CardItem
            values={isSearchObj}
            hasData={interpreterInvoices && interpreterInvoices.length > 0}
            title="Interpreter Invoice Report"
            active={classes.paidStage}
            classes={classes}
            isLoading={loading}
            data={interpreterInvoices}
            dense={dense}
            matches={matches}
          />
        </Paper>
      </div>
    </React.Fragment>
  );
};

export default InterpreterInvoiceReport;
